import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Provider } from "react-redux";
import { useSelector } from "react-redux";
import { ThemeProvider } from "styled-components";
import { ConfigProvider, notification } from "antd";
import store from "./redux/store";
import Profile from "./routes/profile";
import Auth from "./routes/login";
import config from "./config/config";
import ProtectedRoute from "./components/utilities/protectedRoute";
import "./static/css/newStyle.css";
import GlobalStyle from "./styles/globalStyle";
import AutoLogin from "./utility/AutoLogin";
import Spinner from "./components/utilities/Spinner";
import History from "./container/History";
import HistoryDetail from "./container/HistoryDetail";
import QuickAction from "./container/QuickAction";
import Dashboard from "./container/dashboard/Index";
import AdminLayout from "./layout/AdminLayout";
import ForgotPassword from "./container/login/ForgotPassword";
import { listenToForegroundMessages } from "./config/firebase";

const { theme } = config;

const AppRoutes = () => {
  const isLoggedIn = useSelector((state) => state.auth.login);

  const rtl = false;
  const topMenu = true;
  const darkMode = false;
  // const location = useLocation();

  // console.log('location',location)

  const [locationAllowed, setLocationAllowed] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    requestLocationIOSSafe().then((res) => {
      if (!isMounted) return;

      if (res.status === "OK") {
        console.log("Location OK:", res);
        setLocationAllowed(true);
        setError("");
        return;
      }

      if (res.status === "PERMISSION_DENIED") {
        setError(
          "Location access is required. Please enable location permission and reload.",
        );
        return;
      }

      if (res.status === "NOT_SUPPORTED") {
        setError("Geolocation is not supported by this browser.");
        return;
      }

      setError(
        "Unable to detect location. Please move to an open area and try again.",
      );
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;

    let unsubscribe;
    let isMounted = true;

    listenToForegroundMessages((payload) => {
      notification.info({
        message: payload?.notification?.title || "Notification",
        description: payload?.notification?.body || "",
      });
    }).then((listener) => {
      if (!isMounted && listener) {
        listener();
        return;
      }

      unsubscribe = listener;
    });

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, [isLoggedIn]);

  const requestLocationIOSSafe = (retries = 2) => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ status: "NOT_SUPPORTED" });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            status: "OK",
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (err) => {
          // Permission denied
          if (err.code === 1) {
            resolve({ status: "PERMISSION_DENIED" });
            return;
          }

          // iOS: location unknown → retry
          if (err.code === 2 && retries > 0) {
            setTimeout(() => {
              requestLocationIOSSafe(retries - 1).then(resolve);
            }, 2000);
            return;
          }

          // Final failure
          resolve({ status: "LOCATION_UNAVAILABLE" });
        },
        {
          enableHighAccuracy: true,
          timeout: 2000,
          maximumAge: 0,
        },
      );
    });
  };

  // /* ❌ BLOCK APP */
  // if (!locationAllowed) {
  //     return (
  //         <div style={{ textAlign: "center", marginTop: "100px" }}>
  //             <h2>Location Permission Required</h2>
  //             <p>{error}</p>
  //             <button onClick={requestLocationIOSSafe}>
  //                 Allow Location
  //             </button>
  //         </div>
  //     );
  // }

  return (
    <ConfigProvider
      direction={rtl ? "rtl" : "ltr"}
      theme={{
        token: {
          fontFamily:
            'Geist, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
      }}
    >
      <ThemeProvider theme={{ ...theme, rtl, topMenu, darkMode }}>
        <GlobalStyle />
        <Routes>
          <Route
            path="/login"
            element={
              !isLoggedIn ? <Auth /> : <Navigate to="/dashboard" replace />
            }
          />
          <Route path="/check/:token" element={<AutoLogin />} />
          <Route path="/forgotPassword" element={<ForgotPassword />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/profile/*" element={<Profile />} />
            <Route element={<AdminLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/quick-actions" element={<QuickAction />} />
              <Route path="/History" element={<History />} />
              <Route path="/History/details" element={<HistoryDetail />} />
            </Route>
            {/* other protected routes */}
          </Route>

          <Route
            path="*"
            element={
              <Navigate to={isLoggedIn ? "/dashboard" : "/login"} replace />
            }
          />
        </Routes>
      </ThemeProvider>
    </ConfigProvider>
  );
};

const App = () => (
  <>
    <Provider store={store}>
      <Router basename={process.env.PUBLIC_URL}>
        <AppRoutes />
      </Router>
    </Provider>
  </>
);

export default App;
